//src/utils/index.ts
import { fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { Auth } from 'aws-amplify';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { FieldValues, UseFormGetValues } from 'react-hook-form';

const APP_VERSION = '7';

export const prepareHeaders = (headers: Headers) => {
  headers.set('x-app-version', APP_VERSION);

  return Auth.currentSession()
    .then((session) => {
      const token = session.getAccessToken();
      // Further parsing:
      if (token) {
        headers.set('Authorization', `Bearer ${token.getJwtToken()}`);
      }
      return headers;
    })
    .catch(() => {
      return headers;
    });
};

export const staggeredBaseQueryWithBailOut = (baseUrl: string) =>
  retry(
    async (args, api, extraOptions) => {
      const result = await fetchBaseQuery({ baseUrl, prepareHeaders })(
        args,
        api,
        extraOptions,
      );
      // bail out of re-tries immediately if unauthorized,
      // because we know successive re-retries would be redundant

      if (result.error?.status === 426) {
        window.location.reload();
      }
      if (
        result.error?.status === 400 ||
        result.error?.status === 401 ||
        result.error?.status === 403 ||
        result.error?.status === 500
      ) {
        retry.fail(result.error);
      }

      return result;
    },
    {
      maxRetries: 1,
    },
  );

export function withPayloadType<T>() {
  return (t: T) => ({ payload: t });
}

export function getLabel(
  getValues?: UseFormGetValues<FieldValues>,
  label?:
    | string
    | ((values?: { [x: string]: any }) => string | React.ReactNode),
  questionnaireYear?: string,
) {
  if (typeof label === 'string') {
    const labelHasPlaceHolder = /{\w+}/g.test(label || '');
    const placeholders = labelHasPlaceHolder
      ? label!.match(/{\w+}/g)?.map((m) => m.replace(/[{, }]/g, ''))
      : undefined;
    const labelTransformed =
      labelHasPlaceHolder && placeholders?.length
        ? label?.replace(
            /{\w+}/g,
            (getValues && getValues(placeholders[0])) || questionnaireYear,
          )
        : label;

    return labelTransformed;
  }
  if (typeof label === 'function' && getValues) {
    const values = getValues();

    return label({ ...values, year: questionnaireYear || values.year });
  }
}

export const scrollToChildElement = (
  container: HTMLElement,
  child: HTMLElement,
) => {
  container.scrollTop =
    child.offsetTop + child.clientHeight / 2 - container.clientHeight / 2;
};

export const formatDate = (
  date?: string | Date,
  format: 'backend' | 'us-date' | 'long-us-date' = 'backend',
) => {
  if (!date) return '';
  const Date = moment(date);
  return format === 'backend'
    ? Date.format('DD-MM-YYYY')
    : format === 'us-date'
    ? Date.format('MM/DD/YYYY')
    : Date.format('MMM D, YYYY [at] h:mm A');
};

export function downloadFile(url: string, fileName?: string) {
  const headers = new Headers();

  return Auth.currentSession()
    .then((session) => {
      const token = session.getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token.getJwtToken()}`);
      }
      return headers;
    })
    .then((headers) =>
      fetch(process.env.REACT_APP_API_URL + url, {
        headers,
      }),
    )
    .then(async (response: Response) => {
      const blob = await response.blob();
      const fileName =
        response.headers
          .get('content-disposition')
          ?.split('filename=')[1]
          .split(';')[0] || '';
      return { blob, fileName };
    })
    .then(({ blob, fileName: responseFileName }) => {
      const name = fileName ? fileName : responseFileName;
      const file = new File([blob], name);
      saveAs(file, name);

      return file;
    });
}

export async function bulkDownloadFolder(
  folderId: number,
  fileName: string,
  onStatus?: (status: string) => void,
) {
  const headers = new Headers();
  const prepared = await prepareHeaders(headers);

  const startUrl =
    process.env.REACT_APP_API_URL + `/file_objects/${folderId}/download_all`;
  let response = await fetch(startUrl, { headers: prepared });

  if (
    response.headers.get('content-type')?.includes('application/zip') ||
    response.headers.get('content-disposition')
  ) {
    const blob = await response.blob();
    saveAs(blob, fileName);
    return;
  }

  const json = await response.json();
  if (!json.bulk_download_id) {
    throw new Error(json.error || 'Failed to start download');
  }

  let status = json.status;
  const bulkId = json.bulk_download_id as number;
  onStatus && onStatus(status);
  while (status === 'pending' || status === 'processing') {
    await new Promise((r) => setTimeout(r, 3000));
    response = await fetch(
      process.env.REACT_APP_API_URL + `/bulk_downloads/${bulkId}`,
      { headers: prepared },
    );

    if (response.redirected) {
      const blob = await response.blob();
      saveAs(blob, fileName);
      onStatus && onStatus('complete');
      return;
    }

    const poll = await response.json();
    status = poll.status;
    onStatus && onStatus(status);
    if (status === 'failed') {
      throw new Error('Download failed');
    }
  }
}
